import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// On session expiry / not-authenticated, bounce to login (except auth checks themselves).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    if (
      err.response?.status === 401 &&
      !url.includes("/auth/") &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/login")
    ) {
      window.location.assign("/login");
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Auth & Teams ----
export const authRegister = (body) => api.post("/auth/register", body).then((r) => r.data);
export const authLogin = (body) => api.post("/auth/login", body).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authVerifyEmail = (token) => api.post("/auth/verify-email", { token }).then((r) => r.data);
export const authResendVerification = (email) => api.post("/auth/resend-verification", { email }).then((r) => r.data);
export const authForgotPassword = (email, turnstile_token) => api.post("/auth/forgot-password", { email, turnstile_token }).then((r) => r.data);
export const authResetPassword = (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const getTeam = () => api.get("/team").then((r) => r.data);
export const inviteMember = (email) => api.post("/team/invite", { email }).then((r) => r.data);
export const getInvite = (token) => api.get(`/team/invite/${token}`).then((r) => r.data);
export const acceptInvite = (body) => api.post("/team/invite/accept", body).then((r) => r.data);


export const getContracts = (params) => api.get("/contracts", { params }).then((r) => r.data);
export const getContract = (id) => api.get(`/contracts/${id}`).then((r) => r.data);
export const getStats = (params) => api.get("/contracts/stats", { params }).then((r) => r.data);
export const getCategories = () => api.get("/categories").then((r) => r.data);
export const runScan = (body) => api.post("/scan", body).then((r) => r.data);
export const getScanStatus = () => api.get("/scan/status").then((r) => r.data);
export const runRenewalScan = () => api.post("/renewals/scan").then((r) => r.data);
export const getRenewalScanStatus = () => api.get("/renewals/scan/status").then((r) => r.data);
export const getRenewals = (params) => api.get("/renewals", { params }).then((r) => r.data);
export const getRenewalStats = () => api.get("/renewals/stats").then((r) => r.data);
export const generateBid = (id, regenerate = false) =>
  api.post(`/contracts/${id}/bid`, null, { params: { regenerate } }).then((r) => r.data);
export const getBidStatus = (id) => api.get(`/contracts/${id}/bid/status`).then((r) => r.data);
export const generateActionPlan = (id, regenerate = false) =>
  api.post(`/contracts/${id}/action-plan`, null, { params: { regenerate } }).then((r) => r.data);
export const uploadItt = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/contracts/${id}/itt`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteItt = (id) => api.delete(`/contracts/${id}/itt`).then((r) => r.data);
export const getBidProbability = (id, regenerate = false) =>
  api.post(`/contracts/${id}/bid-probability`, null, { params: { regenerate } }).then((r) => r.data);
export const saveBid = (id, bid_template) =>
  api.put(`/contracts/${id}/bid`, { bid_template }).then((r) => r.data);
export const exportBidUrl = (id, format) => `${API}/contracts/${id}/bid/export?format=${format}`;
export const getFrameworks = () => api.get("/frameworks").then((r) => r.data);
export const getPortals = () => api.get("/portals").then((r) => r.data);
export const setPortalRegistration = (name, registered) =>
  api.put("/portals/registration", { name, registered }).then((r) => r.data);
export const runFrameworksHealthCheck = () => api.post("/frameworks/health/check").then((r) => r.data);
export const getFrameworksHealthStatus = () => api.get("/frameworks/health/status").then((r) => r.data);
export const setFrameworkRegistration = (name, registered) =>
  api.put("/frameworks/registration", { name, registered }).then((r) => r.data);
export const getReadiness = () => api.get("/readiness").then((r) => r.data);
export const saveReadiness = (id, body) => api.put(`/readiness/${id}`, body).then((r) => r.data);
export const setPipeline = (id, body) => api.put(`/contracts/${id}/pipeline`, body).then((r) => r.data);
export const setSubmission = (id, item_id, done) =>
  api.put(`/contracts/${id}/submission`, { item_id, done }).then((r) => r.data);
export const archiveContract = (id, archived = true) =>
  api.put(`/contracts/${id}/archive`, { archived }).then((r) => r.data);
export const favouriteContract = (id, favourite = true) =>
  api.put(`/contracts/${id}/favourite`, { favourite }).then((r) => r.data);
export const getPipeline = () => api.get("/pipeline").then((r) => r.data);
export const getDeadlines = (within_days = 7) =>
  api.get("/deadlines", { params: { within_days } }).then((r) => r.data);

export const daysUntil = (deadline) => {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d)) return null;
  return Math.ceil((d - new Date()) / 86400000);
};
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const saveSettings = (body) => api.put("/settings", body).then((r) => r.data);
export const previewDigest = (min_score) =>
  api.get("/digest/preview", { params: { min_score } }).then((r) => r.data);
export const sendDigest = (body) => api.post("/digest/send", body).then((r) => r.data);

export const fmtValue = (amount) =>
  typeof amount === "number" && amount
    ? `£${amount.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
    : "Value not stated";

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
